/**
 * Alerts — what Trusstor's notification engine raises for one person in one
 * project, as D-ORCA names them (docs/specs/2026-10-18-alerts-design.md).
 *
 * The backend translates Trusstor's types into these kinds in its adapter, and
 * both clients draw and configure alerts by them: the strings must agree on
 * every side, so they live here once. Not the dashboard's *activity alerts*,
 * which D-ORCA computes itself (`../alerts`).
 */

/** The groups the settings pages are laid out by, in display order. */
export const ALERT_GROUPS = ['safety', 'reports', 'activities', 'issues'] as const;
export type AlertGroup = (typeof ALERT_GROUPS)[number];

/** Every kind, in the order its group lists it. */
export const ALERT_KINDS = [
  'safety.distress',
  'safety.restrictedArea',
  'reports.dailyOverview',
  'reports.tagHealth',
  'activities.planUpdated',
  'activities.statusUpdated',
  'issues.createdSummary',
  'issues.commented',
  'issues.assigned',
  'issues.statusChanged',
] as const;
export type AlertKind = (typeof ALERT_KINDS)[number];

/** How an alert reaches a person. */
export const ALERT_CHANNELS = ['inApp', 'sms', 'push'] as const;
export type AlertChannel = (typeof ALERT_CHANNELS)[number];

/** The cadence of the two digest kinds. */
export const ALERT_FREQUENCIES = ['daily', 'weekly'] as const;
export type AlertFrequency = (typeof ALERT_FREQUENCIES)[number];

export interface IAlertKindSpec {
  group: AlertGroup;
  /** The channels the engine can deliver this kind on. */
  channels: readonly AlertChannel[];
  /** Set when the person picks daily or weekly: what the engine starts with. */
  defaultFrequency?: AlertFrequency;
}

const IN_APP_PUSH = ['inApp', 'push'] as const;
const ALL_CHANNELS = ALERT_CHANNELS;

export const ALERT_KIND_SPECS: Record<AlertKind, IAlertKindSpec> = {
  'safety.distress': { group: 'safety', channels: ALL_CHANNELS },
  'safety.restrictedArea': { group: 'safety', channels: ALL_CHANNELS },
  'reports.dailyOverview': { group: 'reports', channels: IN_APP_PUSH },
  'reports.tagHealth': { group: 'reports', channels: IN_APP_PUSH, defaultFrequency: 'weekly' },
  'activities.planUpdated': { group: 'activities', channels: IN_APP_PUSH },
  'activities.statusUpdated': { group: 'activities', channels: IN_APP_PUSH },
  'issues.createdSummary': { group: 'issues', channels: IN_APP_PUSH, defaultFrequency: 'daily' },
  'issues.commented': { group: 'issues', channels: IN_APP_PUSH },
  'issues.assigned': { group: 'issues', channels: IN_APP_PUSH },
  'issues.statusChanged': { group: 'issues', channels: IN_APP_PUSH },
};

export function alertGroupOf(kind: AlertKind): AlertGroup {
  return ALERT_KIND_SPECS[kind].group;
}

export function isAlertKind(value: string): value is AlertKind {
  return (ALERT_KINDS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// One alert, as the stream carries it
// ---------------------------------------------------------------------------

/** What each kind says beyond its name. Absent when the record carried nothing readable. */
export interface IAlertDetailsByKind {
  'safety.distress': { tagId: string; workerName?: string; floor?: string; area?: string };
  'safety.restrictedArea': { tagId: string; tagNick: string; workerName?: string; floor?: string; area?: string };
  'reports.dailyOverview': Record<string, never>;
  'reports.tagHealth': { lowBatteryTags: string[]; unseenTags: string[] };
  'activities.planUpdated': { byName: string; dateFrom?: string };
  'activities.statusUpdated': { byName: string; itemName: string; status: string; areas: string[] };
  'issues.createdSummary': { weekly: boolean; descriptions: string[] };
  'issues.commented': { issueId: string; description: string; byName: string; comment?: string; attachment: boolean };
  'issues.assigned': { issueId: string; description: string; byName?: string };
  'issues.statusChanged': { issueId: string; description: string; status: string; previousStatus: string; byName?: string };
}

interface IAlertOf<K extends AlertKind> {
  kind: K;
  /** The engine's record id — unique, stable. */
  id: string;
  /** When the engine raised it — ISO. */
  at: string;
  viewed: boolean;
  details?: IAlertDetailsByKind[K];
}

export type IAlert = { [K in AlertKind]: IAlertOf<K> }[AlertKind];

/** The two alert frames the notifications stream adds when it names a site. */
export type AlertsStreamFrame =
  | { channel: 'alerts.reset'; data: { siteId: string; alerts: IAlert[] } }
  // `removed`: alerts dismissed — gone from the list. `viewed`: alerts now
  // seen, or `all` of them. Both arrive from the person's other tabs and
  // devices (and from Trusstor's own apps), so every screen stays in step
  // without reopening anything.
  | { channel: 'alerts.upsert'; data: IAlertsUpsert };

export interface IAlertsUpsert {
  siteId: string;
  alerts: IAlert[];
  removed?: string[];
  viewed?: 'all' | string[];
}

/** A change the person made to their alerts, told to their other streams. */
export interface IAlertsChange {
  viewed?: 'all' | string[];
  removed?: string[];
}

// ---------------------------------------------------------------------------
// Settings — the Alerts Hub and the personal page
// ---------------------------------------------------------------------------

/** `<kind>` or, for one restricted area, `safety.restrictedArea:<areaId>`. */
export type AlertSettingKey = AlertKind | `safety.restrictedArea:${string}`;

const RESTRICTED_AREA_PREFIX = 'safety.restrictedArea:';

export function restrictedAreaKey(restrictedAreaId: string): AlertSettingKey {
  return `${RESTRICTED_AREA_PREFIX}${restrictedAreaId}`;
}

/** The parts of a setting key, or `null` when it names nothing. */
export function parseAlertSettingKey(key: string): { kind: AlertKind; restrictedAreaId?: string } | null {
  if (key.startsWith(RESTRICTED_AREA_PREFIX)) {
    const restrictedAreaId = key.slice(RESTRICTED_AREA_PREFIX.length);
    return restrictedAreaId ? { kind: 'safety.restrictedArea', restrictedAreaId } : null;
  }
  return isAlertKind(key) ? { kind: key } : null;
}

/** A place, a set of professions closed off from it, and the certificates that let a worker in anyway. */
export interface IRestrictedArea {
  id: string;
  floors: { id: string; name: string }[];
  areas: { id: string; name: string; floorId: string }[];
  /** `all`: closed to every profession. */
  professions: 'all' | { id: string; tradeId: string }[];
  /** `all`: any certificate lets a worker in. Empty: none does. */
  certificates: 'all' | { id: string; name: string }[];
}

/** What a restricted area is created or edited with. */
export interface IRestrictedAreaInput {
  floorIds: string[];
  areaIds: string[];
  professionIds: 'all' | string[];
  certificateIds: 'all' | string[];
}

/** What a restricted area can be made of: the project's places, professions and certificates. */
export interface IRestrictedAreaOptions {
  floors: { id: string; name: string }[];
  areas: { id: string; name: string; floorId: string }[];
  professions: { id: string; tradeId: string; contractor?: string }[];
  certificates: { id: string; name: string }[];
}

export interface IAlertSettingRow {
  key: AlertSettingKey;
  kind: AlertKind;
  channels: Partial<Record<AlertChannel, boolean>>;
  frequency?: AlertFrequency;
  /** Set on the row of one restricted area. */
  restrictedArea?: IRestrictedArea;
  /** Who gets it — the Alerts Hub only. Trusstor usernames. */
  recipients?: string[];
}

export interface IAlertSettingsGroup {
  group: AlertGroup;
  rows: IAlertSettingRow[];
}

export interface IAlertSettingsResponse {
  groups: IAlertSettingsGroup[];
  /** The Alerts Hub only: the members who may receive safety alerts —
   *  decided by the server's role policy, never by the client. */
  safetyEligible?: string[];
}

export interface IAlertSettingUpdate {
  channels?: Partial<Record<AlertChannel, boolean>>;
  frequency?: AlertFrequency;
}

export interface IAlertsHubUpdate extends IAlertSettingUpdate {
  recipients?: { add?: string[]; remove?: string[] };
}
